import { useStrategiesContext, type IParametersEnumValues } from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useRequestPreTradeCompareMutation } from '../../../redux/preTradeApi';
import type { PreTradeRequest, PreTradeResponseData } from '../../../redux/preTradeApiTypes';
import { mapPretradeCompareResponse, toPreTradeOrder, type SnipeSweepBand } from './utils';

/** Strategy parameter name carrying the urgency enum on Sniper-class strategies. */
const URGENCY_PARAMETER_NAME = 'Urgency';

export interface UseCompareUrgenciesResult {
  /** One band per successfully returned urgency, sorted by urgency index ascending. */
  bands: SnipeSweepBand[];
  isLoading: boolean;
  /** False when we can't determine the urgency enum values yet (strategies still loading or strategy unknown). */
  canCompare: boolean;
  /** Aggregate error message when any fan-out request failed. */
  error: string | undefined;
  /** Fires the fan-out. */
  compare: () => Promise<void>;
  /** Wipes cached comparison results so the chart reverts to the single-band view. */
  reset: () => void;
}

/**
 * Fan-out hook for the Compare button.
 * Looks up the active strategy's `Urgency` enum values, clones the current
 * scenario's NewOrderSingle into one request per urgency, fires them in
 * parallel through the silent compare endpoint, and turns the responses into
 * chart-ready `SnipeSweepBand[]`.
 *
 * No staleness guard: navigating to a different scenario unmounts this view, and
 * the Compare button is disabled while `isLoading`, so a fan-out cannot race a
 * scenario change or a double-fire from this UI.
 */
export function useCompareUrgencies(scenario: PreTradeResponseData | undefined): UseCompareUrgenciesResult {
  const { isLoaded: strategiesLoaded, strategiesByName } = useStrategiesContext();
  const [requestPreTradeCompare] = useRequestPreTradeCompareMutation();
  const [bands, setBands] = useState<SnipeSweepBand[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  /**
   * The strategy's Urgency parameter EnumValues, sorted by Index ascending.
   * Empty when strategies haven't loaded yet, when the strategy is unknown,
   * or when the strategy doesn't expose an Urgency parameter.
   */
  const urgencyEnumValues = useMemo<IParametersEnumValues[]>(() => {
    if (!strategiesLoaded || !scenario) {
      return [];
    }
    const strategyName = scenario.NewOrderSingle?.Strategy ?? scenario.Strategy;
    if (!strategyName) {
      return [];
    }
    const strategy = strategiesByName.get(strategyName);
    const urgencyParam = strategy?.Parameters.find(p => p.Name === URGENCY_PARAMETER_NAME);
    const values = urgencyParam?.EnumValues ?? [];
    return values.toSorted((a, b) => a.Index - b.Index);
  }, [strategiesLoaded, strategiesByName, scenario]);

  const canCompare = urgencyEnumValues.length > 0;

  const reset = useCallback(() => {
    setBands([]);
    setError(undefined);
    setIsLoading(false);
  }, []);

  const compare = useCallback(async () => {
    if (!scenario || urgencyEnumValues.length === 0) {
      return;
    }
    setIsLoading(true);
    setError(undefined);

    const sessionId = scenario.SessionIdentifier;
    if (!sessionId) {
      setError('Missing session identifier on scenario');
      setIsLoading(false);
      return;
    }

    // Source the base order from the scenario itself, not the OMS form, so Compare
    // remains usable after the user navigates the form away from this Sniper order.
    const baseOrder = toPreTradeOrder(scenario);
    if (!baseOrder) {
      setError('Scenario is missing the original order payload');
      setIsLoading(false);
      return;
    }

    const settled = await Promise.allSettled(
      urgencyEnumValues.map(enumValue => {
        const request: PreTradeRequest = {
          SessionID: sessionId,
          Order: {
            ...baseOrder,
            // Backend deserializes Urgency as `int32` (see WSStrategyParameters); must be a number.
            Parameters: { ...baseOrder.Parameters, Urgency: enumValue.Index },
          },
        };
        return requestPreTradeCompare(request).unwrap();
      })
    );

    const { bands: newBands, failures } = mapPretradeCompareResponse(settled, urgencyEnumValues);
    setBands(newBands);
    setError(failures.length ? `Failed to compare ${failures.join(', ')}` : undefined);
    setIsLoading(false);
  }, [scenario, urgencyEnumValues, requestPreTradeCompare]);

  return { bands, isLoading, canCompare, error, compare, reset };
}
